<template>

    <ThemeChanger :to="isLightTheme ? Theme.Default : Theme.Dark" :background="caseStudy?.default_background_color">

        <Grid class="mt-32 lg:hidden">

            <Column class="sm:items-center sm:text-center">

                <AOS type="fade-up">

                    <Heading tag="h1" v-html="caseStudy?.heading"/>

                </AOS>

                <AOS type="fade-up" v-if="caseStudy?.client_name">

                    <Paragraph class="mt-10 uppercase" v-html="caseStudy?.client_name"/>

                </AOS>

                <AOS type="fade-up">

                    <Paragraph class="mt-10" v-html="caseStudy?.description"/>

                </AOS>

            </Column>

        </Grid>

        <Picture :image="caseStudy?.cover" class="mt-24 lg:hidden" center auto width="100vw" maxWidth="100vw" height="auto" :lazy="false"/>

        <CustomCursor :text="cursor.scroll">

            <template #default="{ eventListeners }">

                <div class="case-study__cover-shell" v-bind="eventListeners" ref="caseStudyElement" @click="scrollToContent" :style="`height: ${coverHeight}`">

                    <div class="case-study__cover z-10" :style="`height: ${coverHeight}; max-height: 100vh`">

                            <div
                                class="absolute top-0 z-0"
                                :style="{
                                    height: coverHeight,
                                    maxHeight: '100vh',
                                    width: '100vw',
                                    maxWidth: '100vw',
                                    background: caseStudy?.cover?.gradient_background ?? 'linear-gradient(140deg, #121212, #232323)'
                                }"
                            />

                            <Picture
                                v-if="caseStudy?.cover"
                                width="100vw"
                                maxWidth="100vw"
                                maxHeight="100vh"
                                cover
                                cover-fixed
                                :lazy="false"
                                :image="caseStudy?.cover"/>

                    </div>

                    <div class="relative z-20 md:h-full md:flex md:items-center">

                        <Container fluid class="items-center w-full">

                            <Grid fluid class="w-full">

                                <Column :span="3" class="flex items-center">

                                    <div class="px-10 w-full flex-col justify-center" style="width: 450px">

                                        <AOS type="fade-up" is-group>

                                            <Heading tag="h1" no-margin v-html="caseStudy?.heading"/>

                                        </AOS>

                                        <AOS type="fade-up" v-if="caseStudy?.client_name" is-group delay="250">

                                            <Paragraph class="mt-10 uppercase" v-html="caseStudy?.client_name"/>

                                        </AOS>

                                        <AOS type="fade-up" is-group delay="500">

                                            <Paragraph class="mt-10" v-html="caseStudy?.description"/>

                                        </AOS>

                                        </div>

                                    </Column>

                                <Column :span="2"/>

                                <Column :span="2"/>

                            </Grid>

                        </Container>

                    </div>

                </div>

            </template>

        </CustomCursor>

    </ThemeChanger>

    <BodyContent
        v-for="(item, idx) of caseStudy?.body"
        :data="item"
        :key="idx"
        uses-custom-themes
        :single-space="idx > 0 ? caseStudy?.body[idx - 1].type === 'heading_and_content' && item.type === 'heading_and_content' : false"
        :is-last="idx == caseStudy?.body.length - 1"
        :default-background="caseStudy?.default_background_color"
        :default-gradient="caseStudy?.default_gradient_background"
        :fallback-theme="isLightTheme ? Theme.Default : Theme.Dark"/>

    <ThemeChanger :to="Theme.Dark">

        <ContactHeader
            class="pt-16 md:pt-24"
            v-if="caseStudy?.contact_heading"
            :heading="caseStudy?.contact_heading"
            :label="caseStudy?.contact_label"
            :route="caseStudy?.contact_entry?.slug"/>

        <AOS type="fade" easing="ease" :duration="800" :delay="500">

            <Grid class="mt-24 md:mt-48" v-if="caseStudy?.related_cases_heading">

                <Column>

                    <Paragraph v-html="caseStudy?.related_cases_heading" heading/>

                </Column>

            </Grid>

        </AOS>

        <Projects
            :key="caseStudy?.heading"
            v-if="caseStudy?.related_cases"
            class="sm:mt-12 md:mt-16"
            :data="caseStudy?.related_cases"/>

    </ThemeChanger>

    <MetaInfo v-if="caseStudy?.seo" :seo="caseStudy.seo" :structured-data="caseStudy.seo_structured_data"/>

</template>

<script lang="ts">

    import { defineComponent, ref, watchEffect, getCurrentInstance, nextTick, onMounted, onUnmounted, computed } from 'vue'
    import BodyContent from '../components/BodyContent.vue'
    import Column from '../components/Grid/Column.vue'
    import Grid from '../components/Grid/Grid.vue'
    import Container from '../components/Container.vue'
    import CustomCursor from '../components/CustomCursor.vue'
    import Heading from '../components/Heading.vue'
    import ContactHeader from '../components/ContactHeader.vue'
    import Paragraph from '../components/Paragraph.vue'
    import Picture from '../components/Picture.vue'
    import Projects from '../components/Projects.vue'
    import MetaInfo from '../components/MetaInfo.vue'
    import ThemeChanger from '../components/ThemeChanger.vue'
    import { useCms } from '../compositions/UseCms'
    import { useTheme } from '../compositions/UseTheme'
    import { scrollTo } from '../compositions/HeaderComposition'
    import { useTranslation } from '../compositions/UseTranslation'
    import { Theme } from '../enums/Theme'
    import { useRoute, useRouter } from 'vue-router'
    import { CaseStudyInterface } from '../Interfaces/CaseStudyInterface'
    import { themeColor } from '../themeColor'
    import { Route } from '../enums/Route'

    export default defineComponent({
        name: 'CaseStudy',
        components: {
            Container,
            BodyContent,
            Column,
            CustomCursor,
            Grid,
            Heading,
            Paragraph,
            Picture,
            Projects,
            ContactHeader,
            ThemeChanger,
            MetaInfo
        },
        setup() {

            const { setTheme, setBackground } = useTheme()
            const refreshAOSHard = getCurrentInstance()?.appContext.config.globalProperties.$aos?.refreshHard
            const { isCNLocale } = useCms()
            const route = useRoute()
            const router = useRouter()
            const caseStudy = ref<CaseStudyInterface | null>(null)
            const caseStudyElement = ref<HTMLDivElement | null>(null)
            const { cursor } = useTranslation()
            const coverHeight = ref<string>('1000px')

            useTheme().initializeCurrentThemeBasedOnRouteMeta()

            watchEffect(() => {

                if (route.name === Route.CaseStudy) {

                    const path = `/cms/${ isCNLocale.value ? 'cn' : 'en' }/cases/${ route.params.slug }.json`

                    fetch(path)
                        .then(response => response.json())
                        .then(response => {

                            caseStudy.value = response

                            setTheme(caseStudy?.value?.theme?.value ?? Theme.Dark)
                            setBackground(caseStudy?.value?.default_background_color ?? themeColor[caseStudy?.value?.theme?.value ?? Theme.Dark])

                            calcCoverHeight()

                        })
                        .then(() => nextTick())
                        .then(() => {

                            refreshAOSHard?.()

                        })
                        .catch(err => {

                            console.log(err)
                            router.replace({ name: Route.NotFound })

                        })

                }

            })

            const scrollToContent = async() => {

                if (caseStudyElement.value) {

                    const boundingBox = caseStudyElement.value.getBoundingClientRect()

                    await scrollTo(
                        document.documentElement,
                        window.scrollY,
                        window.scrollY + boundingBox.top + window.innerHeight,
                        500
                    )

                }

            }

            function calcCoverHeight() {

                const originalWidth = caseStudy?.value?.cover?.desktop_asset?.width
                const originalHeight = caseStudy?.value?.cover?.desktop_asset?.height
                const ratio = window.innerWidth / originalWidth

                if (originalHeight && originalWidth && ratio) {

                    coverHeight.value = `${ Math.floor(originalHeight * ratio) }px`

                } else {

                    coverHeight.value = '100vh'

                }

            }

            onMounted(() => {

                window.addEventListener('resize', calcCoverHeight)

            })

            onUnmounted(() => {

                window.removeEventListener('resize', calcCoverHeight)

            })

            return {
                Theme,
                caseStudy,
                caseStudyElement,
                scrollToContent,
                cursor,
                coverHeight,
                isLightTheme: computed(() => {

                    console.log('isLight: ', caseStudy?.value?.theme?.value === 'light')
                    return caseStudy?.value?.theme?.value === 'light' as Theme

                })
            }

        }
    })
</script>

<style lang="scss">

    @include component(case-study) {

        @include part(cover-shell) {

            @apply relative mx-auto w-screen max-h-screen items-center hidden;

            @screen lg {

                display: block;

            }

            backface-visibility: hidden;

        }

        @include part(cover) {

            @apply absolute top-0 left-0 right-0 bottom-0;

        }

        @include part(description) {

            @include option(enter-from, leave-to) {

                transform: translateY(20px);
                opacity: 0;

            }

            @include option(enter-active) {

                transition-delay: .7s !important;

            }

        }

    }

</style>
